import { Injectable, inject } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	Router,
	RouterStateSnapshot
} from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import {
	MercuryRoles,
	SharedRoles
} from 'src/app/services/enums/user-groups.enum';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { ServicesStartupService } from 'src/app/services/services-startup/services-startup.service';
import { SwalPopupService } from 'src/app/services/swal-popup-service/swal-popup.service';
import {
	JwtPayload,
	decodeJwtPayload
} from 'src/app/utilities/jwt/jwt.utility';

interface AccessJwtPayload extends JwtPayload {
	groups?: unknown;
}

@Injectable({
	providedIn: 'root'
})
export class CanLoginGuard {
	private swalPopupService = inject(SwalPopupService);
	private oidcSecurityService = inject(OidcSecurityService);
	private environmentService = inject(EnvironmentService);
	private router = inject(Router);
	private serviceStartupService = inject(ServicesStartupService);

	canActivate(
		_route: ActivatedRouteSnapshot,
		_state: RouterStateSnapshot
	): Observable<boolean> {
		return this.serviceStartupService.mainServicesLoaded$.pipe(
			filter((isLoaded) => isLoaded),
			switchMap(() => this.canAccess())
		);
	}

	private canAccess() {
		return this.parseJwt().pipe(
			switchMap((token) =>
				this.environmentService.environment$.pipe(
					filter((environment) => environment != ''),
					map((environment) => {
						const groups = this.getGroups(token);

						if (this.isAllowed(environment, groups)) {
							if (
								this.router
									.getCurrentNavigation()
									?.finalUrl?.toString() == '/home/news' &&
								groups.includes(MercuryRoles.customerUser) &&
								localStorage.getItem('isLogin') == 'true'
							) {
								localStorage.removeItem('isLogin');
								void this.router.navigate([
									'/home/mercury/content/preview/promotional-material'
								]);
								return false;
							}
							return true;
						}

						void this.swalPopupService
							.noAccess(environment)
							.then((result) => {
								if (result.isConfirmed) {
									this.oidcSecurityService
										.logoff()
										.subscribe();
								}
							});
						return false;
					})
				)
			)
		);
	}

	private getGroups(token: AccessJwtPayload | null): string[] {
		return Array.isArray(token?.groups)
			? token.groups.filter(
					(group): group is string => typeof group === 'string'
				)
			: [];
	}

	private isAllowed(environment: string, groups: string[]): boolean {
		environment = environment.toLowerCase();

		return (
			(environment == 'staging' &&
				groups.includes(SharedRoles.stagingAccess)) ||
			(environment == 'production' &&
				groups.includes(SharedRoles.portalAccess))
		);
	}

	private parseJwt(): Observable<AccessJwtPayload | null> {
		return this.oidcSecurityService
			.getAccessToken()
			.pipe(
				map(
					(token) =>
						decodeJwtPayload(token) as AccessJwtPayload | null
				)
			);
	}
}
